import React from 'react';
import { useSite } from '../context/SiteContext';
import { useLang } from '../context/LangContext';
import { pickI18n } from '../i18n/helpers';
import { ArrowUpRight } from 'lucide-react';
import AnimatedBackground from './AnimatedBackground';

const About = () => {
  const { content } = useSite();
  const { t, lang } = useLang();
  const aboutData = content?.about || {};
  const P = (v) => pickI18n(v, lang);
  return (
    <section id="nosotros" className="relative py-20 sm:py-28 bg-[#0a1622] overflow-hidden">
      <AnimatedBackground variant="aurora" intensity={0.35} />
      {/* Decorative gradient */}
      <div className="absolute -left-32 top-1/4 w-96 h-96 rounded-full bg-[#00e0c7]/5 blur-3xl" />
      <div className="absolute right-0 bottom-0 w-[500px] h-[500px] rounded-full bg-[#00a3ff]/[0.04] blur-3xl" />

      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 relative">
        <div className="grid lg:grid-cols-12 gap-10 sm:gap-12 lg:gap-16 items-start">
          {/* Left: Image */}
          <div className="lg:col-span-5">
            <div className="relative rounded-3xl overflow-hidden">
              <img
                src={aboutData.image}
                alt="Cinema interior"
                className="w-full h-[380px] sm:h-[480px] lg:h-[540px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a1622] via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 sm:bottom-6 sm:left-6 sm:right-6">
                <div className="backdrop-blur-md bg-[#0d1b2a]/60 border border-white/10 rounded-2xl p-4 sm:p-5">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#00e0c7] mb-2">
                    {t('about.established')}
                  </div>
                  <div className="text-white text-sm leading-relaxed">
                    {t('about.quote')}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-3 gap-3">
              {['DCI', 'SMPTE', 'AES-128'].map((label) => (
                <div
                  key={label}
                  className="py-3 sm:py-4 rounded-xl border border-white/5 bg-white/[0.02] text-center"
                >
                  <div className="text-[#00e0c7] font-display text-base sm:text-lg">{label}</div>
                  <div className="text-[9px] sm:text-[10px] text-white/40 uppercase tracking-wider mt-1">
                    {t('about.certified')}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Content */}
          <div className="lg:col-span-7">
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#00e0c7] mb-5">
              {t('sections.about')}
            </div>
            <h2 className="font-display text-white text-2xl sm:text-3xl lg:text-[52px] font-light tracking-tight" style={{ lineHeight: 1.25 }}>
              {P(aboutData.title)}
              {pickI18n(aboutData.titleAccent, lang) && (
                <>
                  {' '}
                  <span className="italic text-[#5eead4]">{P(aboutData.titleAccent)}</span>
                </>
              )}
            </h2>

            <p className="mt-6 sm:mt-8 text-white/65 text-[15px] sm:text-[16px] leading-[1.75]">
              {P(aboutData.intro)}
            </p>

            <div className="mt-8 pl-5 sm:pl-6 border-l-2 border-[#00e0c7]/40">
              <div className="text-[11px] uppercase tracking-[0.25em] text-[#00e0c7] mb-2">
                {t('sections.ourMission')}
              </div>
              <p className="text-white text-[16px] sm:text-[17px] italic font-light leading-relaxed">
                {P(aboutData.mission)}
              </p>
            </div>

            <div className="mt-10 sm:mt-12 space-y-4 sm:space-y-6">
              <div className="text-[11px] uppercase tracking-[0.3em] text-white/40 mb-2">
                {t('sections.ourPillars')}
              </div>
              {(aboutData.pillars || []).map((p) => (
                <div
                  key={p.number}
                  className="group flex gap-4 sm:gap-6 py-4 sm:py-5 border-t border-white/5 hover:border-[#00e0c7]/30 transition-colors"
                >
                  <div className="text-[#00e0c7]/60 font-display text-xl sm:text-2xl font-light w-10 sm:w-12 flex-shrink-0">
                    {p.number}
                  </div>
                  <div className="flex-1">
                    <h4 className="text-white font-medium text-[16px] sm:text-[17px] mb-1.5">
                      {P(p.title)}
                    </h4>
                    <p className="text-white/55 text-[13px] sm:text-[14px] leading-relaxed">{P(p.text)}</p>
                  </div>
                  <ArrowUpRight className="w-5 h-5 text-white/20 group-hover:text-[#00e0c7] group-hover:rotate-45 transition-all flex-shrink-0" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
